import { FUtil, FServiceAPI, FI18n } from '@freelog/tools-lib';
import fCenterMessage from '@/components/fCenterMessage';
import { fOnOffFeedback } from '@/components/fOnOffFeedback';
import fBatchHandleResource_ResultModal from '@/components/fBatchHandleResource_ResultModal';

// ─── Types ───────────────────────────────────────────────────────────

export interface ResourceTypeFilter {
  value: string;
  label: string;
  values: string[];
  labels: string[];
}

/** 资源/合集/收藏列表「资源类型」筛选项初始值；在 i18n 异步加载完成后由 global 再 dispatch 一次以替换壳翻译 */
export function defaultResourceTypeCodesFilterAll(): ResourceTypeFilter {
  return {
    value: '#all',
    // 资源类型筛选项「全部」
    label: FI18n.i18nNext.t('filter_resourcetype_option_all'),
    values: ['#all'],
    labels: [
      // 资源类型筛选项「全部」
      FI18n.i18nNext.t('filter_resourcetype_option_all'),
    ],
  };
}

export interface ResourceListApiParams {
  skip: number;
  limit: number;
  keywords?: string;
  resourceType?: string;
  resourceTypeCode?: string;
  resourceTypeCategory?: 1 | 2;
  status?: 0 | 1 | 2 | 4;
  isSelf: 1;
  subjectType: 1 | 4;
}

export type BatchUpdatePayload = {
  status?: 1 | 4;
  addPolicies?: {
    policyName: string;
    policyText: string;
    status: 0 | 1;
  }[];
};

type BatchUpdateResult = {
  [resourceID: string]: {
    data: any;
    status: 1 | 2;
  };
};

// ─── Pure helpers ────────────────────────────────────────────────────

/**
 * Parse resource type filter into API parameters.
 * Shared between resourceListPage and collectionListPage.
 */
export function parseResourceTypeFilters(resourceTypeCodes: ResourceTypeFilter): {
  resourceTypeCode: string;
  resourceTypeCategory: 1 | 2;
  resourceType: string;
} {
  const resourceTypes: string[] = resourceTypeCodes.value.split('#');
  let resourceTypeCode: string = resourceTypes[0];
  let resourceTypeCategory: 1 | 2 = 1;
  let resourceType: string = '';

  if (resourceTypes.length > 1 && resourceTypes[resourceTypes.length - 1] === 'other') {
    resourceTypeCode = resourceTypes[0];
    resourceTypeCategory = 2;
  }

  if (resourceTypes.length > 1 && resourceTypes[resourceTypes.length - 1] === 'all') {
    resourceType = resourceTypeCodes.labels[resourceTypeCodes.labels.length - 2];
  }

  return { resourceTypeCode, resourceTypeCategory, resourceType };
}

/**
 * Build common API params for FServiceAPI.Resource.list.
 */
export function buildResourceListApiParams(opts: {
  listLength: number;
  keywords: string;
  resourceTypeCodes: ResourceTypeFilter;
  resourceStatus: 0 | 1 | 2 | 4 | '#';
  subjectType: 1 | 4;
}): ResourceListApiParams {
  const { resourceTypeCode, resourceTypeCategory, resourceType } =
    parseResourceTypeFilters(opts.resourceTypeCodes);

  return {
    skip: opts.listLength,
    limit: FUtil.Predefined.pageSize,
    keywords: opts.keywords || undefined,
    resourceType: resourceType || undefined,
    resourceTypeCode: resourceType === '' ? resourceTypeCode || undefined : undefined,
    resourceTypeCategory: resourceType === '' ? resourceTypeCategory : undefined,
    status: opts.resourceStatus === '#' ? undefined : (opts.resourceStatus as 0),
    isSelf: 1,
    subjectType: opts.subjectType,
  };
}

/**
 * Show success/failure feedback after batch update.
 * Returns true if all succeeded, false if some failed.
 */
export async function handleBatchUpdateFeedback(
  data: BatchUpdateResult,
  payload: BatchUpdatePayload,
): Promise<boolean> {
  const allSucceeded = Object.values(data).every((d) => d.status === 1);

  if (allSucceeded) {
    if (payload.addPolicies !== undefined) {
      fCenterMessage({ message: '策略添加成功' });
    } else if (payload.status === 1) {
      fOnOffFeedback({ state: 'on', message: '上架成功' });
    } else if (payload.status === 4) {
      fOnOffFeedback({ state: 'off', message: '下架成功' });
    }
    return true;
  }

  if (payload.addPolicies !== undefined) {
    await fBatchHandleResource_ResultModal({ type: 'addPolicy', dataSource: data });
  } else if (payload.status === 1) {
    await fBatchHandleResource_ResultModal({ type: 'online', dataSource: data });
  } else if (payload.status === 4) {
    await fBatchHandleResource_ResultModal({ type: 'offline', dataSource: data });
  }
  return false;
}

/**
 * Fetch info for failed resources after batch update.
 */
export async function fetchFailedResourceInfo(
  data: BatchUpdateResult,
): Promise<
  {
    resourceId: string;
    policies: { policyId: string; policyName: string; status: 0 | 1 }[];
    status: 1 | 2 | 3 | 4;
  }[]
> {
  const failedResourceIDs: string[] = Object.entries(data)
    .filter((d) => d[1].status === 2)
    .map((d) => d[0]);

  if (failedResourceIDs.length === 0) return [];

  const params = { resourceIds: failedResourceIDs.join(',') };
  const { data: failData } = await FServiceAPI.Resource.batchInfo(params);
  return failData;
}

/**
 * Map batch update results back onto the resource list.
 * Used identically by both resourceListPage and collectionListPage.
 */
export function applyBatchUpdateToList<T extends { id: string; policy: string[]; status: any }>(
  resourceList: T[],
  batchResult: BatchUpdateResult,
  failResources: { resourceId: string; policies: any[]; status: any }[],
): T[] {
  return resourceList.map((rl) => {
    if (batchResult[rl.id] && batchResult[rl.id].status === 1) {
      return {
        ...rl,
        policy: batchResult[rl.id].data.policies
          .filter((l: any) => l.status === 1)
          .map((l: any) => l.policyName),
        status: batchResult[rl.id].data.status,
      };
    }

    const failResource = failResources.find((fr) => fr.resourceId === rl.id);
    if (failResource) {
      return {
        ...rl,
        policy: failResource.policies
          .filter((l: any) => l.status === 1)
          .map((l: any) => l.policyName),
        status: failResource.status,
      };
    }
    return rl;
  });
}
